import { useEffect, useRef, useState } from 'react'
import { MONTH_YEAR_MONTHS } from '#app/utils/month-year'

export { MONTH_YEAR_MONTHS } from '#app/utils/month-year'
const COLUMN_SCROLL_CLASS =
	'scrollbar-thumb-[#32302F] scrollbar-track-[#F3F3F3] relative min-h-0 overflow-y-auto overscroll-contain transition-colors [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[#4a4847]'

/**
 * Shared Month / Year scroll columns — used by MonthYearPicker,
 * MonthYearRangePicker, and Study Level graduation step.
 */
export function MonthYearColumns({
	selectedMonth,
	selectedYear,
	onMonthChange,
	onYearChange,
	years,
	minDate,
	maxDate,
	monthsRef: monthsRefProp,
	yearsRef: yearsRefProp,
}: {
	selectedMonth: number
	selectedYear: number
	onMonthChange: (month: number) => void
	onYearChange: (year: number) => void
	years: number[]
	minDate?: Date
	maxDate?: Date
	monthsRef?: React.RefObject<HTMLDivElement | null>
	yearsRef?: React.RefObject<HTMLDivElement | null>
}) {
	const localMonthsRef = useRef<HTMLDivElement>(null)
	const localYearsRef = useRef<HTMLDivElement>(null)
	const monthsRef = monthsRefProp ?? localMonthsRef
	const yearsRef = yearsRefProp ?? localYearsRef

	useEffect(() => {
		const center = (col: HTMLDivElement | null, selector: string) => {
			const item = col?.querySelector(selector) as HTMLElement | null
			if (!col || !item) return
			col.scrollTop =
				item.offsetTop - col.clientHeight / 2 + item.offsetHeight / 2
		}
		const run = () => {
			center(monthsRef.current, `[data-month="${selectedMonth}"]`)
			center(yearsRef.current, `[data-year="${selectedYear}"]`)
		}
		run()
		const frame = requestAnimationFrame(run)
		return () => cancelAnimationFrame(frame)
	}, [selectedMonth, selectedYear, monthsRef, yearsRef])

	const isMonthDisabled = (m: number) => {
		const firstOfMonth = new Date(selectedYear, m, 1)
		const lastOfMonth = new Date(selectedYear, m + 1, 0)
		if (minDate && lastOfMonth < minDate) return true
		if (maxDate && firstOfMonth > maxDate) return true
		return false
	}

	const isYearDisabled = (y: number) => {
		if (maxDate && y > maxDate.getFullYear()) return true
		if (minDate && y < minDate.getFullYear()) return true
		return false
	}

	return (
		<>
			<div className="grid grid-cols-2 gap-4 rounded-tl-[10px] rounded-tr-[10px] border-b border-gray-200 bg-[#32302F] px-3 py-3">
				<div className="text-left font-medium text-[#FFFFFF]">Month</div>
				<div className="text-left font-medium text-[#FFFFFF]">Year</div>
			</div>

			<div className="grid h-[250px] grid-cols-2 gap-2 overflow-hidden rounded-br-[10px] rounded-bl-[10px] border border-[#F3F3F3]">
				<div
					ref={monthsRef}
					data-scroll-lock-scrollable
					className={COLUMN_SCROLL_CLASS}
					onWheel={(event) => event.stopPropagation()}
					onTouchMove={(event) => event.stopPropagation()}
				>
					{MONTH_YEAR_MONTHS.map((m, i) => {
						const disabled = isMonthDisabled(i)
						return (
							<button
								key={m}
								type="button"
								data-month={i}
								disabled={disabled}
								onClick={() => onMonthChange(i)}
								className={`w-full cursor-pointer px-3 py-2 text-left text-[14px] transition-colors ${
									selectedMonth === i
										? 'bg-[#F3F3F3] font-medium text-gray-900'
										: 'text-gray-700 hover:bg-gray-100'
								} ${disabled ? 'cursor-not-allowed opacity-40 hover:bg-transparent' : ''}`}
							>
								{m}
							</button>
						)
					})}
				</div>

				<div
					ref={yearsRef}
					data-scroll-lock-scrollable
					className={COLUMN_SCROLL_CLASS}
					onWheel={(event) => event.stopPropagation()}
					onTouchMove={(event) => event.stopPropagation()}
				>
					{years.map((y) => {
						const disabled = isYearDisabled(y)
						return (
							<button
								key={y}
								type="button"
								data-year={y}
								disabled={disabled}
								onClick={() => onYearChange(y)}
								className={`w-full cursor-pointer px-3 py-2 text-left text-[14px] transition-colors ${
									selectedYear === y
										? 'bg-[#F3F3F3] font-medium text-gray-900'
										: 'text-gray-700 hover:bg-gray-100'
								} ${disabled ? 'cursor-not-allowed opacity-40 hover:bg-transparent' : ''}`}
							>
								{y}
							</button>
						)
					})}
				</div>
			</div>
		</>
	)
}

/**
 * Month + Year picker popover — two scrollable columns (Month / Year) plus a
 * Confirm button. Used for the work-authorization expiration fields, where only
 * the month/year matters (the committed value is the 1st of the chosen month).
 *
 * UI + open behaviour mirror the shared DurationPicker ("Total Duration Lived
 * At Address"): it renders fixed, aligned to the trigger's width, opens below
 * (flipping up only when there's no room), and scrolls the selected values into
 * view on open. Drop-in for `PrimeMoveInCalendar`: same
 * `value` / `onChange` / `triggerRef` contract; `showFlexibleToggle` is accepted
 * but ignored so existing call sites need no edits.
 */
export function MonthYearPicker({
	value,
	onChange,
	triggerRef,
	minDate,
	maxDate,
	title = 'Select Date',
}: {
	value?: Date
	onChange: (date: Date) => void
	triggerRef?: React.RefObject<HTMLElement | null>
	minDate?: Date
	maxDate?: Date
	/** Heading shown above the Month / Year columns. */
	title?: string
	/** Ignored — kept for drop-in parity with PrimeMoveInCalendar. */
	showFlexibleToggle?: boolean
}) {
	const now = new Date()
	const currentYear = now.getFullYear()
	const startYear = minDate?.getFullYear() ?? currentYear - 5
	const endYear = maxDate?.getFullYear() ?? currentYear + 30
	const years = Array.from(
		{ length: Math.max(1, endYear - startYear + 1) },
		(_, i) => startYear + i,
	)

	const pickerRef = useRef<HTMLDivElement>(null)
	const [styles, setStyles] = useState<React.CSSProperties>({
		position: 'fixed',
		zIndex: 50,
		opacity: 0,
	})
	const [selectedMonth, setSelectedMonth] = useState(
		value ? value.getMonth() : now.getMonth(),
	)
	const [selectedYear, setSelectedYear] = useState(
		value ? value.getFullYear() : currentYear,
	)

	const PICKER_HEIGHT = 420

	useEffect(() => {
		const update = () => {
			if (!triggerRef?.current) return
			const rect = triggerRef.current.getBoundingClientRect()
			const spaceBelow = window.innerHeight - rect.bottom
			const openTop = spaceBelow < PICKER_HEIGHT
			setStyles({
				position: 'fixed',
				left: rect.left,
				width: rect.width,
				zIndex: 50,
				...(openTop
					? { bottom: window.innerHeight - rect.top + 8 }
					: { top: rect.bottom + 8 }),
			})
		}
		update()
		const timer = setTimeout(update, 10)
		window.addEventListener('scroll', update, true)
		window.addEventListener('resize', update)
		return () => {
			clearTimeout(timer)
			window.removeEventListener('scroll', update, true)
			window.removeEventListener('resize', update)
		}
	}, [triggerRef])

	useEffect(() => {
		if (!triggerRef?.current) return
		const rect = triggerRef.current.getBoundingClientRect()
		const spaceBelow = window.innerHeight - rect.bottom
		if (spaceBelow < PICKER_HEIGHT) {
			window.scrollBy({
				top: PICKER_HEIGHT - spaceBelow + 16,
				behavior: 'smooth',
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<div ref={pickerRef} className="z-50" style={styles}>
			<div className="w-full rounded-2xl border border-gray-200 bg-[#FBFDFD] p-4 shadow-lg">
				<div className="mb-2 text-start">
					<h3 className="font-regular text-md text-gray-800">{title}</h3>
				</div>

				<MonthYearColumns
					selectedMonth={selectedMonth}
					selectedYear={selectedYear}
					onMonthChange={setSelectedMonth}
					onYearChange={setSelectedYear}
					years={years}
					minDate={minDate}
					maxDate={maxDate}
				/>

				<div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
					<div className="text-sm text-gray-600">
						Date : {MONTH_YEAR_MONTHS[selectedMonth]} {selectedYear}
					</div>
					<button
						type="button"
						onClick={() => onChange(new Date(selectedYear, selectedMonth, 1))}
						className="cursor-pointer rounded-full bg-[#32302f] px-6 py-2 font-medium text-white transition-colors hover:bg-black"
					>
						Confirm
					</button>
				</div>
			</div>
		</div>
	)
}
